import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Globe,
  MessageSquare,
  PhoneCall,
  Plus,
  Share2,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { Button } from '@/components/button';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { PageHeader } from '@/components/page-header';
import { Pager } from '@/components/pager';
import { Select } from '@/components/select';
import { SkeletonList } from '@/components/skeleton';
import { StatusPill } from '@/components/status-dot';
import { dollars, formatStatus } from '@/lib/format';
import { LEAD_STAGES, useLeads, type LeadStage } from '@/lib/leads';

export const SOURCE_ICON: Record<string, ComponentType<LucideProps>> = {
  web_form: Globe,
  sms: MessageSquare,
  voice: PhoneCall,
  referral: Share2,
  manual: UserPlus,
};

export const SOURCE_LABEL: Record<string, string> = {
  web_form: 'Website',
  sms: 'SMS',
  voice: 'Phone',
  referral: 'Referral',
  manual: 'Manual',
};

export function stageTone(stage: string): 'success' | 'warning' | 'neutral' | 'danger' {
  if (stage === 'won') return 'success';
  if (stage === 'proposal') return 'warning';
  if (stage === 'lost') return 'danger';
  return 'neutral';
}

type StageFilter = '' | LeadStage;

export function LeadsIndex() {
  const [q, setQ] = useState('');
  const [stage, setStage] = useState<StageFilter>('');
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = useLeads({
    q: q || undefined,
    stage: stage || undefined,
    page,
  });
  const data = paginated?.data;
  const meta = paginated?.meta;
  const navigate = useNavigate();

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="Leads"
        subtitle="Inbound inquiries and active opportunities — before they become clients."
        action={
          <Link to="/leads/new">
            <Button>
              <Plus className="size-4" /> New lead
            </Button>
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search name, email, phone…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={stage}
          onChange={(e) => {
            setStage(e.target.value as StageFilter);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All stages</option>
          {LEAD_STAGES.map((s) => (
            <option key={s} value={s}>
              {formatStatus(s)}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No leads yet"
          description="Drop in a manual lead, or wire up the website inquiry form to feed this list automatically."
          action={
            <Link to="/leads/new">
              <Button>
                <Plus className="size-4" /> New lead
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {data.map((l) => {
              const Icon = SOURCE_ICON[l.source] ?? UserPlus;
              return (
                <DataListRow
                  key={l.id}
                  onClick={() => navigate(`/leads/${l.id}`)}
                  interactive
                >
                  <Icon
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-label={SOURCE_LABEL[l.source] ?? l.source}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate font-medium text-foreground">{l.name}</span>
                      {l.email ? (
                        <span className="truncate text-xs text-muted-foreground">{l.email}</span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{SOURCE_LABEL[l.source] ?? l.source}</span>
                      {l.phone ? (
                        <>
                          <span aria-hidden>·</span>
                          <span>{l.phone}</span>
                        </>
                      ) : null}
                      {l.value_estimate_cents != null ? (
                        <>
                          <span aria-hidden>·</span>
                          <span className="tabular-nums">{dollars(l.value_estimate_cents)}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <StatusPill tone={stageTone(l.stage)}>{formatStatus(l.stage)}</StatusPill>
                </DataListRow>
              );
            })}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
